import React, { useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import {
  selectQuestionScoresQuery,
  selectOrgRubricScoresQuery,
  selectOrgTrendsQuery,
} from 'js/store/reducers/queries';
import _ from 'lodash';
import {
  lookupQuestionScores,
  lookupOrgRubricScores,
  lookupOrgTrends,
  orgScoresQueryKey,
} from 'js/actions/org-actions';
import OrgComponentDetailRouter from '../components/OrgComponent/OrgComponentDetailRouter';
import { connect } from 'react-redux';
import { selectActiveWorkspace } from 'js/store/reducers/workspaces';
import {
  isOrgRubricKey,
  rubricTeamsToQuestionScores,
  rubricDocumentToOrgTemplate,
} from 'js/utils/rubric-org-scores';

function OrgAssessmentDetailContainer({
  orgId,
  assessmentKey,
  questionScoresQuery,
  orgRubricScoresQuery,
  orgTrendsQuery,
  lookupQuestionScores,
  lookupOrgRubricScores,
  lookupOrgTrends,
  match,
  location,
  organization,
  workspace,
  templates,
  assessmentHistorySummaries,
}) {
  const isRubric = useMemo(
    () => isOrgRubricKey(assessmentKey, assessmentHistorySummaries),
    [assessmentKey, assessmentHistorySummaries]
  );

  const shouldLoadQuestionScores =
    !isRubric &&
    !questionScoresQuery?.data &&
    !questionScoresQuery?.query?.error &&
    questionScoresQuery?.query?.isLoading !== true;

  const shouldLoadOrgRubricScores =
    isRubric &&
    !orgRubricScoresQuery?.data &&
    !orgRubricScoresQuery?.query?.error &&
    orgRubricScoresQuery?.query?.isLoading !== true;

  const shouldLoadOrgTrends =
    !isRubric &&
    !orgTrendsQuery?.data &&
    !orgTrendsQuery?.query?.error &&
    orgTrendsQuery?.query?.isLoading !== true;

  const query = useMemo(
    () => Object.fromEntries(new URLSearchParams(location.search)),
    [location.search]
  );

  useEffect(() => {
    if (shouldLoadQuestionScores) {
      lookupQuestionScores(orgId, assessmentKey, query).catch(_.noop);
    }
  }, [
    lookupQuestionScores,
    orgId,
    assessmentKey,
    query,
    shouldLoadQuestionScores,
  ]);

  useEffect(() => {
    if (shouldLoadOrgRubricScores) {
      lookupOrgRubricScores(orgId, assessmentKey, query).catch(_.noop);
    }
  }, [
    lookupOrgRubricScores,
    orgId,
    assessmentKey,
    query,
    shouldLoadOrgRubricScores,
  ]);

  useEffect(() => {
    if (shouldLoadOrgTrends) {
      lookupOrgTrends(orgId, assessmentKey).catch(_.noop);
    }
  }, [lookupOrgTrends, orgId, assessmentKey, shouldLoadOrgTrends]);

  const questionScoresForDisplay = useMemo(() => {
    if (!isRubric) {
      return questionScoresQuery;
    }

    const payload = orgRubricScoresQuery.data;
    const adapted = payload?.teams
      ? rubricTeamsToQuestionScores(payload.teams, payload.document)
      : null;

    return {
      query: orgRubricScoresQuery.query,
      data: adapted,
    };
  }, [isRubric, questionScoresQuery, orgRubricScoresQuery]);

  const rubricTemplate = useMemo(() => {
    if (!isRubric || !orgRubricScoresQuery.data) {
      return null;
    }

    const { rubric_id, rubric_name, document } = orgRubricScoresQuery.data;
    return rubricDocumentToOrgTemplate(rubric_id, rubric_name, document);
  }, [isRubric, orgRubricScoresQuery.data]);

  const detailLoading = isRubric
    ? orgRubricScoresQuery.query?.isLoading ?? true
    : questionScoresQuery.query?.isLoading ??
      orgTrendsQuery.query?.isLoading ??
      true;

  const detailError =
    questionScoresQuery.query?.error || orgTrendsQuery.query?.error;

  return (
    <OrgComponentDetailRouter
      match={match}
      questionScoresQuery={{
        ...questionScoresForDisplay,
        query: {
          ...(questionScoresForDisplay.query ?? {}),
          isLoading: detailLoading,
          error: detailError ?? questionScoresForDisplay.query?.error,
        },
      }}
      orgRubricScoresQuery={orgRubricScoresQuery}
      orgTrendsQuery={orgTrendsQuery}
      organization={organization}
      workspace={workspace}
      templates={templates}
      rubricTemplate={rubricTemplate}
      isRubric={isRubric}
    />
  );
}

export default withRouter(
  connect(
    (store, ownProps) => {
      const orgId = ownProps.match.params.orgId;
      const assessmentKey = ownProps.match.params.assessmentKey;
      const queryKey = `${orgId}-${assessmentKey}`;
      const combinedKey = orgScoresQueryKey(orgId, assessmentKey);

      return {
        orgId,
        assessmentKey,
        assessmentHistorySummaries: ownProps.assessmentHistorySummaries ?? [],
        questionScoresQuery: selectQuestionScoresQuery(store, combinedKey),
        orgRubricScoresQuery: selectOrgRubricScoresQuery(store, combinedKey),
        orgTrendsQuery: selectOrgTrendsQuery(store, queryKey),
        organization: ownProps.organization,
        workspace: selectActiveWorkspace(store),
      };
    },
    {
      lookupQuestionScores,
      lookupOrgRubricScores,
      lookupOrgTrends,
    }
  )(OrgAssessmentDetailContainer)
);
